import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import API_BASE from "../api";
import {
  getDeletedAccountAuthMessage,
  isDeletedAccountAuthError,
} from "../auth/deletedAccountMessage";
import {
  clearOAuthCompletionSession,
  completeOAuthProfile,
} from "../auth/oauthFlow";
import { getLocalizedRequiredConsentTexts } from "../content/legal/localizedConsentTexts";
import countries from "../data/countries";
import {
  validateCity,
  validateCountry,
  validateRequiredConsents,
  validateUsername,
} from "./register/RegisterValidation";
import "../styles/pages/OAuth.css";

function getCitySuggestionLabel(option) {
  return typeof option === "string" ? option : option?.name || "";
}

function getCitySuggestionCoordinates(option) {
  if (!option || typeof option === "string") return null;

  const lat = Number(option.lat);
  const lng = Number(option.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function renderConsentLabel(consentTextOrLabel) {
  const label =
    typeof consentTextOrLabel === "string"
      ? consentTextOrLabel
      : consentTextOrLabel?.label || "";
  const labelParts = consentTextOrLabel?.labelParts;

  if (labelParts) {
    return (
      <>
        {labelParts.beforeTerms}
        <Link to="/legal/terms">{labelParts.termsLabel}</Link>
        {labelParts.betweenLinks}
        <Link to="/legal/privacy">{labelParts.privacyLabel}</Link>
        {labelParts.afterPrivacy}
      </>
    );
  }

  return label
    .split(/(Terms and Conditions|Privacy Policy)/)
    .map((part, index) => {
      if (part === "Terms and Conditions") {
        return (
          <Link key={`terms-${index}`} to="/legal/terms">
            {part}
          </Link>
        );
      }

      if (part === "Privacy Policy") {
        return (
          <Link key={`privacy-${index}`} to="/legal/privacy">
            {part}
          </Link>
        );
      }

      return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
}

function getCompletionErrorMessage(t, detail, currentValues, consentTexts) {
  if (isDeletedAccountAuthError(detail)) {
    return getDeletedAccountAuthMessage(t);
  }

  if (detail === "username_already_taken") {
    return t("username_already_taken");
  }

  if (detail === "email_already_registered") {
    return t("email_already_registered");
  }

  if (detail === "username_required") {
    return t("username_required", {
      defaultValue: "Username is required.",
    });
  }

  if (detail === "country_required") {
    return validateCountry("", t);
  }

  if (detail === "city_required") {
    return validateCity("", t);
  }

  if (detail === "country_invalid_chars") {
    return validateCountry(currentValues.country, t, { required: false });
  }

  if (detail === "city_invalid_chars") {
    return validateCity(currentValues.city, t, { required: false });
  }

  if (detail === "accountType_required" || detail === "accountType_invalid") {
    return t("account_type_required");
  }

  if (detail === "acceptTermsPrivacy_required") {
    return consentTexts.acceptTermsPrivacy.error;
  }

  if (detail === "acceptProfileVisibility_required") {
    return consentTexts.acceptProfileVisibility.error;
  }

  if (detail === "oauth_provider_not_allowed") {
    return t("oauth_provider_not_allowed", {
      defaultValue:
        "This Google sign-in could not be used. Please try email login.",
    });
  }

  return t("oauth_profile_save_failed", {
    defaultValue: "We could not complete your profile. Please try again.",
  });
}

export default function OAuthProfileCompletion({ setUser }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const consentTexts = getLocalizedRequiredConsentTexts(t);
  const [statusMessage, setStatusMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [username, setUsername] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [accountType, setAccountType] = useState("");
  const [countrySuggestions, setCountrySuggestions] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [selectedCityCoordinates, setSelectedCityCoordinates] = useState(null);
  const [acceptTermsPrivacy, setAcceptTermsPrivacy] = useState(false);
  const [acceptProfileVisibility, setAcceptProfileVisibility] = useState(false);

  const handleCountryChange = (value) => {
    setCountry(value);
    setSelectedCityCoordinates(null);

    if (!value) {
      setCountrySuggestions([]);
      return;
    }

    const filtered = countries.filter((item) =>
      item.toLowerCase().startsWith(value.toLowerCase())
    );
    setCountrySuggestions(filtered.slice(0, 6));
  };

  const handleCityChange = async (value) => {
    setCity(value);
    setSelectedCityCoordinates(null);

    if (!value || value.length < 2) {
      setCitySuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/geo/cities?q=${encodeURIComponent(
          value
        )}&country=${encodeURIComponent(country)}`
      );
      const data = await response.json();
      setCitySuggestions(Array.isArray(data) ? data : []);
    } catch {
      setCitySuggestions([]);
    }
  };

  const handleCancel = async () => {
    await clearOAuthCompletionSession();
    navigate("/login", { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (!username.trim()) {
      alert(
        t("username_required", {
          defaultValue: "Username is required.",
        })
      );
      return;
    }

    const usernameError = validateUsername(username);
    if (usernameError) {
      alert(t(usernameError));
      return;
    }

    const countryError = validateCountry(country, t);
    if (countryError) {
      alert(countryError);
      return;
    }

    const cityError = validateCity(city, t);
    if (cityError) {
      alert(cityError);
      return;
    }

    if (!accountType) {
      alert(t("account_type_required"));
      return;
    }

    const consentError = validateRequiredConsents({
      acceptTermsPrivacy,
      acceptProfileVisibility,
      consentTexts,
    });
    if (consentError) {
      alert(consentError);
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("");

    try {
      const user = await completeOAuthProfile({
        username: username.trim(),
        country: country.trim(),
        city: city.trim(),
        locationLatitude: selectedCityCoordinates
          ? String(selectedCityCoordinates.lat)
          : "",
        locationLongitude: selectedCityCoordinates
          ? String(selectedCityCoordinates.lng)
          : "",
        accountType,
        acceptTermsPrivacy,
        acceptProfileVisibility,
        language: i18n.language?.split("-")[0] || "en",
      });
      setUser(user);
      navigate("/", { replace: true });
    } catch (error) {
      setStatusMessage(
        getCompletionErrorMessage(
          t,
          error?.detail || error?.message,
          {
            country,
            city,
          },
          consentTexts
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="oauth-page">
      <div className="oauth-card oauth-card--profile">
        <div className="oauth-required-badge">
          {t("oauth_required_badge", { defaultValue: "Required" })}
        </div>
        <h1 className="oauth-title">
          {t("oauth_profile_title", { defaultValue: "Complete your profile" })}
        </h1>
        <p className="oauth-intro">
          {t("oauth_profile_intro", {
            defaultValue:
              "Add the required details to start using ReelitPro.",
          })}
        </p>
        <p className="oauth-latin-hint">
          {t("register_required_profile_latin_hint", {
            defaultValue:
              "Please enter your country and city using English/Latin letters only for now.",
          })}
        </p>

        {statusMessage ? <p className="oauth-error">{statusMessage}</p> : null}

        <form className="oauth-profile-form" onSubmit={handleSubmit}>
          <label className="oauth-field-label" htmlFor="oauth-username">
            {t("username")} *
          </label>
          <input
            id="oauth-username"
            name="username"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            className="oauth-input"
            autoComplete="username"
          />
          <p className="oauth-latin-hint">
            {t("username_public_password_warning", {
              defaultValue:
                "Your username is public. Never use a password as your username.",
            })}
          </p>

          <div className="oauth-suggest-field">
            <label className="oauth-field-label" htmlFor="oauth-country">
              {t("country_of_residence", { defaultValue: "Country" })} *
            </label>
            <input
              id="oauth-country"
              type="text"
              value={country}
              onChange={(event) => handleCountryChange(event.target.value)}
              className="oauth-input"
              autoComplete="country-name"
            />
            {countrySuggestions.length > 0 ? (
              <div className="oauth-suggestion-list">
                {countrySuggestions.map((countryOption) => (
                  <button
                    type="button"
                    key={countryOption}
                    className="oauth-suggestion"
                    onClick={() => {
                      setCountry(countryOption);
                      setCountrySuggestions([]);
                      setSelectedCityCoordinates(null);
                    }}
                  >
                    {countryOption}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="oauth-suggest-field">
            <label className="oauth-field-label" htmlFor="oauth-city">
              {t("city_or_town", { defaultValue: "City" })} *
            </label>
            <input
              id="oauth-city"
              type="text"
              value={city}
              onChange={(event) => handleCityChange(event.target.value)}
              className="oauth-input"
              autoComplete="address-level2"
            />
            {citySuggestions.length > 0 ? (
              <div className="oauth-suggestion-list">
                {citySuggestions.map((cityOption) => {
                  const label = getCitySuggestionLabel(cityOption);
                  return (
                    <button
                      type="button"
                      key={`${label}-${cityOption?.country || ""}-${
                        cityOption?.lat || ""
                      }-${cityOption?.lng || ""}`}
                      className="oauth-suggestion"
                      onClick={() => {
                        setCity(label);
                        setSelectedCityCoordinates(
                          getCitySuggestionCoordinates(cityOption)
                        );
                        setCitySuggestions([]);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <label className="oauth-field-label" htmlFor="oauth-account-type">
            {t("account_type", { defaultValue: "Account type" })} *
          </label>
          <select
            id="oauth-account-type"
            value={accountType}
            onChange={(event) => setAccountType(event.target.value)}
            className="oauth-input"
          >
            <option value="">{t("select_account_type")}</option>
            <option value="personal">{t("personal")}</option>
            <option value="business">{t("business")}</option>
          </select>

          <section className="oauth-consent-section">
            <label className="oauth-consent-row">
              <input
                type="checkbox"
                checked={acceptTermsPrivacy}
                onChange={(event) =>
                  setAcceptTermsPrivacy(event.target.checked)
                }
              />
              <span>{renderConsentLabel(consentTexts.acceptTermsPrivacy)}</span>
            </label>
            <p>{consentTexts.acceptTermsPrivacy.helper}</p>

            <label className="oauth-consent-row">
              <input
                type="checkbox"
                checked={acceptProfileVisibility}
                onChange={(event) =>
                  setAcceptProfileVisibility(event.target.checked)
                }
              />
              <span>{consentTexts.acceptProfileVisibility.label}</span>
            </label>
            <p>{consentTexts.acceptProfileVisibility.helper}</p>
          </section>

          <button
            type="submit"
            className="oauth-primary-button"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("oauth_profile_submitting", { defaultValue: "Completing..." })
              : t("oauth_profile_submit", {
                  defaultValue: "Complete and sign in",
                })}
          </button>

          <button
            type="button"
            className="oauth-secondary-button"
            onClick={handleCancel}
          >
            {t("back_to_login", { defaultValue: "Back to login" })}
          </button>
        </form>
      </div>
    </div>
  );
}
